"use client";

import Image from "next/image";
import React, { useState, useEffect, useCallback, memo } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "mr", label: "मराठी" },
  { value: "hi", label: "हिन्दी" },
];

const ShopPartnerHeader = memo(() => {
  const pathname = usePathname();
  const isCustomer = pathname?.startsWith("/buynow-customer-campaign");
  const tPartner = useTranslations("prebookPartner.nav");
  const tCustomer = useTranslations("prebookCustomer.nav");
  const t = isCustomer ? tCustomer : tPartner;

  const NAV_LINKS = isCustomer
    ? [
        // Hidden while their sections are hidden on /buynow-customer-campaign.
        // { href: "#theProblemSection", label: tCustomer("whyKst") },
        // { href: "#whatIsKstSection", label: tCustomer("whatIsKst") },
        // { href: "#priceComparisonSection", label: tCustomer("pricing") },
      ]
    : [
        { href: "#whyPartnerSection", label: tPartner("whyPartner") },
        { href: "#howItWorksSection", label: tPartner("howItWorks") },
        { href: "#partnerPlansSection", label: tPartner("selectPlan") },
      ];

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [language, setLanguage] = useState("en");

  useEffect(() => {
    const cookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("locale="));
    const lang = cookie?.split("=")[1] || "en";
    setLanguage(lang);
  }, []);

  const changeLanguage = (lang: string) => {
    setLanguage(lang);
    document.cookie = `locale=${lang}; path=/; max-age=${31536000}`;
    // Remove hash to prevent scrolling to anchor after reload
    window.location.href = window.location.pathname + window.location.search;
  };

  const toggleMobile = useCallback(() => setMobileOpen((prev) => !prev), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobile();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [closeMobile]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* ========== HEADER / NAVBAR ========== */}
      <header
        className={`fixed top-0 left-0 w-full z-20 bg-img transition-all duration-300 ${
          scrolled ? "shadow-lg bg-[#E8531E]/95" : "shadow-none bg-img"
        }`}
      >
        <div className="container  mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-5">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a
              href="/"
              className="flex items-center gap-2.5 flex-shrink-0 relative z-[102]"
            >
              <div className="w-16 sm:w-20 md:w-24 h-auto">
                <Image
                  src="/images/shopPartner/KST-Home-sinema-logo.png"
                  alt="KST Logo"
                  width={100}
                  height={100}
                  className="h-auto w-auto"
                  priority
                />
              </div>
            </a>
            {/* Desktop Nav (lg+) */}
            <nav className="hidden lg:flex items-center gap-8">
              {NAV_LINKS.map((link) => (
                <a key={link.href} href={link.href} className="nav-link">
                  {link.label}
                </a>
              ))}
              <a
                href="https://kissashurutalkies.com"
                target="_blank"
                className="header-btn ml-2 rounded rounded-tl-2xl rounded-br-2xl"
              >
                {t("visitKissaShuruTalkies")}
              </a>
              <select
                value={language}
                onChange={(e) => changeLanguage(e.target.value)}
                className="ml-2 px-3 py-2 bg-transparent text-white border border-white/30 rounded rounded-tl-2xl rounded-br-2xl text-sm leading-[1.9] font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/50 uppercase"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value} className="text-black">
                    {l.label}
                  </option>
                ))}
              </select>
            </nav>
            {/* Tablet Nav (md–lg) */}
            <nav className="hidden md:flex lg:hidden items-center gap-6">
              {NAV_LINKS.map((link) => (
                <a key={link.href} href={link.href} className="nav-link">
                  {link.label}
                </a>
              ))}
              <select
                value={language}
                onChange={(e) => changeLanguage(e.target.value)}
                className="px-3 py-2 bg-transparent text-white border border-white/30 rounded rounded-tl-2xl rounded-br-2xl text-sm leading-[1.9] font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/50 uppercase"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value} className="text-black">
                    {l.label}
                  </option>
                ))}
              </select>
            </nav>
            {/* Hamburger (mobile) */}
            <button
              className="md:hidden relative z-[102] w-7 h-5 flex flex-col justify-between"
              onClick={toggleMobile}
              aria-label="Toggle menu"
            >
              <span
                className={`block h-[2.5px] w-full bg-white rounded transition-all duration-300 ${
                  mobileOpen ? "rotate-45 translate-y-[9px]" : ""
                }`}
              />
              <span
                className={`block h-[2.5px] w-full bg-white rounded transition-all duration-300 ${
                  mobileOpen ? "opacity-0" : ""
                }`}
              />
              <span
                className={`block h-[2.5px] w-full bg-white rounded transition-all duration-300 ${
                  mobileOpen ? "-rotate-45 -translate-y-[9px]" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </header>
      {/* ========== MOBILE OVERLAY ========== */}
      <div
        className={`fixed inset-0 bg-black/35 z-[98] transition-opacity duration-300 ${
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobile}
      />
      {/* ========== MOBILE MENU (Top-Down) ========== */}
      <div
        className={`fixed top-0 left-0 right-0 z-[99] transition-transform duration-400 shadow-xl ${
          mobileOpen ? "translate-y-0" : "-translate-y-full"
        }`}
        style={{
          background: "linear-gradient(180deg, #E8531E 0%, #D44A18 100%)",
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Top bar: Logo + Close */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-14 h-auto">
              <Image
                src="/images/shopPartner/KST-Home-sinema-logo.png"
                alt="KST Logo"
                width={80}
                height={80}
                className="h-auto w-auto"
              />
            </div>
          </a>
          <button
            onClick={closeMobile}
            className="relative w-7 h-5 flex flex-col justify-center"
            aria-label="Close menu"
          >
            <span className="block h-[2.5px] w-full bg-white rounded rotate-45 absolute" />
            <span className="block h-[2.5px] w-full bg-white rounded -rotate-45 absolute" />
          </button>
        </div>
        {/* Links */}
        <nav>
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block text-white text-center font-semibold text-base uppercase tracking-wider py-4 border-b border-white/10 hover:bg-white/[0.06] transition-colors"
              onClick={closeMobile}
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://kissashurutalkies.com"
            target="_blank"
            className="block text-white text-center rounded rounded-tl-2xl rounded-br-2xl font-semibold text-base uppercase tracking-wider py-4 hover:bg-white/[0.06] transition-colors border-b border-white/10"
            onClick={closeMobile}
          >
            {t("visitKissaShuruTalkies")} ↗
          </a>
          <div className="py-4 px-4 flex justify-center">
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="px-4 py-2 bg-transparent text-white border border-white/30 rounded rounded-tl-2xl rounded-br-2xl text-base leading-[1.9] font-semibold cursor-pointer focus:outline-none uppercase tracking-wider"
            >
              {LANGUAGES.map((l) => (
                <option key={l.value} value={l.value} className="text-black">
                  {l.label}
                </option>
              ))}
            </select>
          </div>
        </nav>
      </div>
    </>
  );
});

export default ShopPartnerHeader;
